import deepEqual from "fast-deep-equal";
import { Atom, atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { Page, InterfacePageFetchResult } from "../types";
import {
  extractAssetId,
  extractInterfacePageId,
  isTemplateInterfacePageId,
} from "../utils";
import { zslAtom } from "../templates";
import { aiSuggestedEditsAtomFamily } from "../nodes";
import { fetchInterfacePageAtomFamily_SUSPENDS } from "./fetchInterfacePageAtomFamily_SUSPENDS";

type Params = {
  nodeId: string;
  interfaceProjectId: string | undefined;
  interfacePageId: string | undefined;
  proposalId?: string | undefined;
};

export const interfacePageAtomFamily_SUSPENDS = atomFamily(
  ({
    nodeId,
    interfaceProjectId,
    interfacePageId,
    proposalId,
  }: Params): Atom<Promise<Page | null>> => {
    const baseAtom: Atom<Promise<Page | null>> = atom(async (get) => {
      if (!interfacePageId && !proposalId) {
        return;
      }

      // if proposalId is provided, we are using the ai suggested edits and it is a template interface page
      const isTemplateInterfacePage =
        isTemplateInterfacePageId(interfacePageId) || proposalId;

      if (isTemplateInterfacePage) {
        const aiProposedEdits = get(aiSuggestedEditsAtomFamily(nodeId));
        const zsl = get(zslAtom);

        if (aiProposedEdits && proposalId) {
          const aiProposed = aiProposedEdits.additions.find(
            (addition) => addition.id === proposalId,
          );

          if (aiProposed) {
            if (aiProposed.zsl?.interfaces) {
              // Find the page in the proposed interface
              const page = aiProposed.zsl.interfaces[0].pages.find(
                (p: Page) => p.id === interfacePageId,
              );
              if (page) {
                return page;
              }
            }
            if (aiProposed?.outline?.interfaces?.[0]?.pages) {
              // Try finding the page in the outline

              const page = aiProposed?.outline.interfaces[0].pages.find(
                (p: Page) => p.id === interfacePageId,
              );
              if (page) {
                return page;
              }
            }
          }
        }

        // Then check template
        if (zsl && zsl.interfaces && interfacePageId) {
          const coreInterfaceProjectId = extractAssetId(
            interfaceProjectId ?? "",
          );

          const interfaceTemplate = zsl.interfaces.find(
            (project) => coreInterfaceProjectId === project.id,
          );

          if (interfaceTemplate && interfaceTemplate.pages) {
            const corePageId = extractInterfacePageId(interfacePageId);

            const pageTemplate = interfaceTemplate.pages.find(
              (page: Page) => corePageId === page.id,
            );

            if (pageTemplate) {
              return pageTemplate as Page;
            }
          }
        }

        // Because we don't want to fetch with template asset id and get a 404 Not Found error
        return;
      }

      // Delegate API retrieval to the shared fetch atom
      const result = (await get(
        fetchInterfacePageAtomFamily_SUSPENDS({
          interfacePageId,
          interfaceProjectId,
        }),
      )) as InterfacePageFetchResult | null | undefined;

      if (result?.type === "success") {
        return result.data;
      }
      return null;
    });

    baseAtom.debugLabel = `interfacePageAtomFamily_SUSPENDS(${interfaceProjectId}-${interfacePageId}-nodeId:${nodeId})`;
    return baseAtom;
  },
  deepEqual,
);
